/**
 * Save / load / list / publish for design documents.
 *
 * Follows the module's established **backend-first, localStorage-fallback**
 * pattern (see `SelfServiceDashboardService`): legacy calls try the API and
 * falls back to localStorage on any HTTP error, so the Design Builder keeps
 * working with no backend — the same guarantee the rest of the self-service
 * module gives. Explicit Save uses the shape component API and propagates
 * failures so a local draft is never reported as a successful database save.
 */

import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { Observable, from, of, timeout } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { unwrapArray, unwrapObject } from '../../shared/self-service-api.utils';

import { readJson, writeJson } from '../../shared/self-service-storage.utils';
import {
  DESIGNS_STORAGE_KEY,
  DESIGN_DRAFT_STORAGE_KEY,
  DESIGN_PROJECTS_STORAGE_KEY,
} from '../design-builder.constants';
import type { DesignDocument, DesignProject } from '../design-builder.types';
import { normalizeDocument, slugify, toStoredDocument } from '../utils/design-json.utils';
import { designOfRow } from '../utils/design-row.utils';
import { DesignApiConfigService } from './design-api.config';

@Injectable()
export class DesignBuilderPersistenceService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(DesignApiConfigService);
  private readonly keycloak = inject(KeycloakService);

  /** Every saved design: `GET /api/dashboard-layers`. */
  listTemplates(): Observable<Record<string, unknown>[]> {
    return this.http.get<unknown>(this.api.readUrl()).pipe(
      timeout(15000),
      map((response) => unwrapArray<unknown>(response, ['items', 'data', 'results', 'rows', 'content'])
        .map(designOfRow)
        .filter((design): design is Record<string, unknown> => !!design)),
    );
  }

  /**
   * One design: `GET /api/dashboard-layers/<dashboardId>`.
   *
   * Keyed by the DASHBOARD id (`test_layers_7eh2fk` — the name slug plus the
   * design id's tail), not the raw design id, because that is the handle the
   * published row and this API share.
   */
  loadTemplate(dashboardId: string): Observable<Record<string, unknown> | null> {
    const url = `${this.api.readUrl().replace(/\/+$/, '')}/${encodeURIComponent(dashboardId)}`;
    return this.http.get<unknown>(url).pipe(timeout(15000), map(designOfRow));
  }


  // ── Projects ───────────────────────────────────────────────────────────────

  /**
   * Projects — the parent that groups several dashboards.
   *
   * localStorage only for now — the backend has no design-projects endpoint yet,
   * so we don't call it (it only produced 404s).
   */
  listProjects(): Observable<DesignProject[]> {
    const rows = this.localProjects();
    return of([...rows].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
  }

  createProject(name: string): Observable<DesignProject> {
    const now = new Date().toISOString();
    const project: DesignProject = {
      id: `prj_${Date.now().toString(36)}`,
      name: name.trim() || 'Untitled project',
      slug: slugify(name),
      createdAt: now,
      updatedAt: now,
    };
    this.writeLocalProject(project);
    return of(project);
  }

  private localProjects(): DesignProject[] {
    return readJson<DesignProject[]>(DESIGN_PROJECTS_STORAGE_KEY, []);
  }

  private writeLocalProject(project: DesignProject): void {
    const projects = this.localProjects();
    const index = projects.findIndex((candidate) => candidate.id === project.id);
    if (index >= 0) projects[index] = project;
    else projects.push(project);
    writeJson(DESIGN_PROJECTS_STORAGE_KEY, projects);
  }

  // ── Load ───────────────────────────────────────────────────────────────────


  // ── Save ───────────────────────────────────────────────────────────────────

  /**
   * Save the complete design JSON. The shape component endpoint upserts by id
   * using POST for both new and existing designs. Keep a recovery draft, but
   * only report success and update the saved cache after the server responds.
   */
  save(document: DesignDocument): Observable<DesignDocument> {
    // Written in the stored shape — a screen's nodes nested in their layer.
    const payload = toStoredDocument(document);
    writeJson(DESIGN_DRAFT_STORAGE_KEY, payload);
    return from(this.userEmail()).pipe(
      // `emailId` is the dashboard-layers API's owner field; everything else is
      // the design exactly as the JSON pane shows it.
      switchMap((emailId) => this.http.post<unknown>(this.api.saveUrl(), { emailId, ...payload })),
      // Ids are minted here, so the design we sent is the truth. The response is
      // only adopted when it really is a document — a bare ack or a status
      // envelope would otherwise be normalised into an empty design.
      map((saved) => normalizeDocument(documentFrom(saved) ?? payload)),
      map((saved) => {
        this.writeLocal(saved);
        return saved;
      }),
    );
  }

  /** The signed-in user, as the save API's `emailId`. Blank when unknown. */
  private async userEmail(): Promise<string> {
    try {
      return (await this.keycloak.isLoggedIn()) ? (await this.keycloak.loadUserProfile()).email || '' : '';
    } catch {
      return '';
    }
  }

  /** Local recovery draft; the explicit Save button persists to the backend. */
  saveDraft(document: DesignDocument): Observable<boolean> {
    return of(writeJson(DESIGN_DRAFT_STORAGE_KEY, toStoredDocument(document)));
  }

  readDraft(): DesignDocument | null {
    const raw = readJson<Record<string, unknown> | null>(DESIGN_DRAFT_STORAGE_KEY, null);
    return raw ? normalizeDocument(raw) : null;
  }

  clearDraft(): void {
    writeJson(DESIGN_DRAFT_STORAGE_KEY, null);
  }

  // ── Publish ────────────────────────────────────────────────────────────────



  // ── Delete ─────────────────────────────────────────────────────────────────


  // ── localStorage mirror ────────────────────────────────────────────────────

  private localDesigns(): DesignDocument[] {
    return readJson<DesignDocument[]>(DESIGNS_STORAGE_KEY, []);
  }


  private localDesign(id: string): DesignDocument | null {
    const found = this.localDesigns().find((design) => design.id === id);
    return found ? normalizeDocument(found as unknown as Record<string, unknown>) : null;
  }

  private writeLocal(document: DesignDocument): void {
    const designs = this.localDesigns();
    const index = designs.findIndex((candidate) => candidate.id === document.id);
    if (index >= 0) designs[index] = document;
    else designs.push(document);
    // Designs can carry inlined image data URIs, so reclaim the draft key first
    // if the quota is tight rather than silently dropping the save.
    writeJson(DESIGNS_STORAGE_KEY, designs, [DESIGN_DRAFT_STORAGE_KEY]);
  }

  private removeLocal(id: string): void {
    writeJson(DESIGNS_STORAGE_KEY, this.localDesigns().filter((design) => design.id !== id));
  }
}

/**
 * The save response as a design, or null when the server answered with
 * something else — an ack, an id, a status envelope. Only a body that still
 * carries `pages` is a design; anything else would normalise into an empty one.
 */
function documentFrom(response: unknown): Record<string, unknown> | null {
  const raw = unwrapObject<Record<string, unknown>>(response, ['data', 'item', 'design', 'dashboardLayer']);
  return raw && Array.isArray(raw['pages']) ? raw : null;
}
